import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Button } from "@material-ui/core";
import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderToolbar
} from "../../_metronic/_partials/controls";
import { getSessions, getSessionState } from "../../api/session";
import { getSurveyResponsesBySession } from "../../api/survey";
import { alertError } from "../../utils/logger";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { COLORS, StateBadge } from "../components/palette";
import {
	computeKpis,
	surveyStats
} from "../modules/ContentManager/sessions/sessionKpis";
import {
	KpiTile,
	ProgressBar,
	ResponsesByRound,
	SurpriseSummaryCard,
	SurveySummary
} from "../modules/ContentManager/sessions/SummaryTab";

const POLL_MS = 10000;

export function DashboardPage() {
	const isAdmin = useIsAdmin();
	const history = useHistory();
	const [sessions, setSessions] = useState([]);
	const [snapshot, setSnapshot] = useState(null);
	const [surveyResponses, setSurveyResponses] = useState([]);
	const [loaded, setLoaded] = useState(false);
	const mounted = useRef(true);

	const load = useCallback(() => {
		return getSessions().then(res => {
			if (res.status !== 200 || !mounted.current) return null;
			const list = res.data || [];
			setSessions(list);
			const active = list.find(s => s.active);
			if (!active) {
				setSnapshot(null);
				setSurveyResponses([]);
				return null;
			}
			return Promise.all([
				getSessionState(active._id),
				// la media de la encuesta necesita las respuestas; si falla, queda el contador del snapshot
				getSurveyResponsesBySession(active._id).catch(() => null)
			]).then(([stateRes, surveyRes]) => {
				if (!mounted.current) return;
				if (stateRes.status === 200) setSnapshot(stateRes.data);
				if (surveyRes && surveyRes.status === 200)
					setSurveyResponses(surveyRes.data || []);
			});
		});
	}, []);

	useEffect(() => {
		mounted.current = true;
		load()
			.catch(error => {
				alertError({
					error: error,
					customMessage: "No se han podido cargar las sesiones."
				});
			})
			.then(() => {
				if (mounted.current) setLoaded(true);
			});
		const interval = setInterval(() => {
			load().catch(() => {
				/* silencioso: se reintenta en el siguiente ciclo */
			});
		}, POLL_MS);
		return () => {
			mounted.current = false;
			clearInterval(interval);
		};
	}, [load]);

	const kpis = snapshot ? computeKpis(snapshot) : null;
	const survey = kpis ? surveyStats(kpis.survey.questions, surveyResponses) : null;
	const session = kpis ? kpis.session : null;

	return (
		<>
			<Card>
				<CardHeader title="Los dos ojos del Daruma · Resumen">
					<CardHeaderToolbar>
						<Link to="/sessions" className="btn btn-light-primary font-weight-bold">
							Ver sesiones
						</Link>
					</CardHeaderToolbar>
				</CardHeader>
				<CardBody>
					<div className="row">
						<KpiTile
							label="Sesiones"
							value={sessions.length}
							sub={`${sessions.filter(s => s.demo).length} de demo`}
							col="col-6 col-md-3"
						/>
						<KpiTile
							label="Sesión activa"
							value={
								<span style={{ fontSize: "20px" }}>
									{session ? session.name : "Ninguna"}
								</span>
							}
							sub={session ? `Código ${session.code}` : "Actívala desde la lista de sesiones"}
							col="col-6 col-md-3"
						/>
						{kpis && (
							<>
								<KpiTile
									label="Fase actual"
									value={<span style={{ fontSize: "20px" }}>{kpis.phase.label}</span>}
									sub={`${kpis.phase.index} de ${kpis.phase.total} fases`}
									col="col-6 col-md-3"
								/>
								<KpiTile
									label="Ojo del Daruma"
									value={`${kpis.phase.eyeProgress} %`}
									col="col-6 col-md-3"
								>
									<ProgressBar value={kpis.phase.eyeProgress} height={8} className="mt-2" />
								</KpiTile>
							</>
						)}
					</div>
				</CardBody>
			</Card>

			<Card>
				<CardHeader
					title={
						session ? (
							<span>
								Sesión activa: {session.name}
								{session.demo && (
									<StateBadge color={COLORS.lightBlue} className="ml-3">
										Demo
									</StateBadge>
								)}
							</span>
						) : (
							"Sesión activa"
						)
					}
				>
					{session && (
						<CardHeaderToolbar>
							<Button
								variant="contained"
								color="primary"
								size="small"
								onClick={() => history.push("/session/" + session._id)}
							>
								Ir al seguimiento
							</Button>
							{isAdmin && (
								<Button
									variant="outlined"
									size="small"
									className="ml-3"
									onClick={() => history.push("/edit-session/" + session._id)}
								>
									Editar sesión
								</Button>
							)}
						</CardHeaderToolbar>
					)}
				</CardHeader>
				<CardBody>
					{!kpis ? (
						<div className="text-center py-6">
							<div className="text-muted mb-4">
								{loaded
									? "No hay ninguna sesión activa."
									: "Cargando la sesión activa…"}
								{loaded && isAdmin && " Activa una desde la lista de sesiones."}
							</div>
							<Link to="/sessions" className="btn btn-primary">
								Ir a sesiones
							</Link>
						</div>
					) : (
						<>
							{/* Indicadores principales */}
							<div className="row">
								<KpiTile
									label="Mesas"
									value={
										<>
											{kpis.tables.registered}
											<span className="text-muted font-size-sm">
												{" "}
												/ {kpis.tables.planned} previstas
											</span>
										</>
									}
									sub={`${kpis.tables.connected} conectadas${
										kpis.tables.simulated ? ` · ${kpis.tables.simulated} simuladas` : ""
									}`}
									col="col-6 col-md-3"
								/>
								<KpiTile
									label="Respuestas de mesa"
									value={kpis.totalResponses}
									sub="en los tres ejes"
									col="col-6 col-md-3"
								/>
								<KpiTile
									label="Pregunta de inicio"
									value={kpis.expectations.total}
									sub="respuestas recogidas por QR"
									col="col-6 col-md-3"
								/>
								<KpiTile
									label="Encuesta"
									value={
										<>
											{kpis.survey.total}
											{survey.globalAvg != null && (
												<span className="text-muted font-size-sm">
													{" "}
													· media {survey.globalAvg} / {survey.globalMax}
												</span>
											)}
										</>
									}
									sub="respuestas de satisfacción"
									col="col-6 col-md-3"
								/>
							</div>

							{/* Respuestas de mesa por eje */}
							<h5 className="font-weight-bolder mt-4 mb-3">Respuestas de mesa por eje</h5>
							<div className="table-responsive mb-6">
								<ResponsesByRound
									rounds={kpis.rounds}
									tablesCount={kpis.tables.registered}
								/>
							</div>

							{/* Votación de apertura y datos clave */}
							<h5 className="font-weight-bolder mt-4 mb-3">
								Votación de apertura y dato clave de cada eje
							</h5>
							<div className="row">
								<div className="col-12 col-xl-6 mb-4">
									<SurpriseSummaryCard
										title="Apertura"
										stats={kpis.opening.surprise}
										tablesCount={kpis.tables.registered}
										revealed={kpis.opening.revealed}
									/>
								</div>
								{kpis.rounds.map(r => (
									<div key={r.index} className="col-12 col-xl-6 mb-4">
										<SurpriseSummaryCard
											title={`Eje ${r.index} · ${r.shortTitle}`}
											stats={r.surprise}
											tablesCount={kpis.tables.registered}
											revealed={r.revealed}
										/>
									</div>
								))}
							</div>

							{/* Encuesta */}
							<h5 className="font-weight-bolder mt-4 mb-3">Encuesta de satisfacción</h5>
							<SurveySummary stats={survey} compact />

							<div className="text-muted font-size-sm mt-4">
								Se actualiza cada {POLL_MS / 1000} s.
							</div>
						</>
					)}
				</CardBody>
			</Card>
		</>
	);
}
